import { PanelRight } from "lucide-react";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useAgentThread } from "@/shared/domain/agents/hooks";
import { RobotoButton } from "@/shared/foundation/primitives";

import styles from "./ThreadOpenOnPageNotice.module.css";
import {
  NOT_YOURS_HINT,
  useOpenThreadInSidebar,
} from "./useOpenThreadInSidebar";

interface ThreadOpenOnPageNoticeProps {
  threadId: string;
}

/**
 * Stands in for the conversation when the sidebar's address and the page's URL
 * name the same thread, so one thread never gets two composers.
 */
export function ThreadOpenOnPageNotice({
  threadId,
}: ThreadOpenOnPageNoticeProps) {
  return (
    // Only the action needs the record, so both fallbacks are the notice minus
    // its button rather than a spinner or an empty pane — the page behind this
    // one is erroring on the same fetch.
    <ErrorBoundary fallback={() => <NoticeShell />}>
      <React.Suspense fallback={<NoticeShell />}>
        <NoticeWithAction threadId={threadId} />
      </React.Suspense>
    </ErrorBoundary>
  );
}

function NoticeShell({ children }: React.PropsWithChildren) {
  return (
    <div className={styles.notice} data-testid="thread-open-on-page-notice">
      <PanelRight className={styles.icon} aria-hidden="true" />
      <p className={styles.headline}>This thread is already open on the page</p>
      <p className={styles.body}>
        A conversation will show in one place at a time, so you never end up
        typing into two chats on the same thread.
      </p>
      {children}
    </div>
  );
}

function NoticeWithAction({ threadId }: ThreadOpenOnPageNoticeProps) {
  const { data: thread } = useAgentThread({ threadId });
  const { isOwnThread, openHere } = useOpenThreadInSidebar(thread);

  return (
    <NoticeShell>
      {isOwnThread ? (
        <RobotoButton variant="secondary" onClick={openHere}>
          Move it here
        </RobotoButton>
      ) : (
        <p className={styles.hint}>{NOT_YOURS_HINT}</p>
      )}
    </NoticeShell>
  );
}
